import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';

import { Add, Search } from '@/components/ui-bridge/icons';
import { orderType } from '@/constants';
import { postCheckUser } from '@/services/izeeService';
import { APP_REDE_IZEE_URL } from '@/config';
import { useUser } from '@/contexts/UserContext';

import classes from './OrderOptionsList.module.scss';
import OrderCardService from '../OrderCardService/OrderCardService';
import OrderCardType from '../OrderCardType/OrderCardType';

const OrderContainer = ({ title, hidden, children, length }) => {
  return (
    <div className={classes.orderCardType} style={{ display: hidden ? 'none' : '' }}>
      <div>
        <h3 className={classes.orderOptionListSubtitle}>
          <FormattedMessage id={title} /> {length > 0 && `(${length})`}
        </h3>
      </div>
      <div className={classes.container}>{children}</div>
    </div>
  );
};

OrderContainer.propTypes = {
  title: PropTypes.string.isRequired,
  hidden: PropTypes.bool,
  children: PropTypes.node,
  length: PropTypes.number,
};

const OrderMainRequests = ({ features, handleOrderOptionSelect, withDescription, hidden }) => {
  const mainRequests = useMemo(() => 
    features.filter(feature => ['VISTORIA_ENTRADA', 'VISTORIA_SAIDA', 'VISTORIA_CAPTURA360'].includes(feature)),
    [features]
  );

  return (
    <OrderContainer title="orderOptionList.mainRequests" hidden={hidden}>
      {mainRequests.map(option => (
        <OrderCardType
          key={option}
          type={orderType.get(option)}
          hasHelp={false}
          withDescription={withDescription}
          handleClick={() => handleOrderOptionSelect(orderType.get(option))}
        />
      ))}
    </OrderContainer>
  );
};

const OrderAllTypes = ({ features, handleOrderOptionSelect, withDescription, hasHelp, hidden }) => {
  const total = useMemo(() => features.filter(feature => feature.includes('VISTORIA')), [features]);

  return (
    <OrderContainer title="orderOptionList.allTypes" hidden={hidden} length={total.length}>
      {total.map(option => (
        <OrderCardType
          key={option}
          type={orderType.get(option)}
          hasHelp={hasHelp(option)}
          withDescription={withDescription}
          handleClick={() => handleOrderOptionSelect(orderType.get(option))}
        />
      ))}
    </OrderContainer>
  );
};

const OrderSearch = ({ features, handleOrderOptionSelect, withDescription, hasHelp, inputText, intl, hidden }) => {
  const results = useMemo(() => {
    if (!inputText) return [];
    
    const normalizeText = text =>
      text
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase();
    
    const normalizedInput = normalizeText(inputText);
    const inputWords = normalizedInput.split(' ');

    return features.filter(feature => {
      if (!feature.includes('VISTORIA')) return false;
      
      const name = normalizeText(intl.formatMessage({ id: orderType.get(feature) }));
      const description = normalizeText(intl.formatMessage({ id: orderType.get(`description.${feature}`) }));
      
      return inputWords.every(word => name.includes(word) || description.includes(word));
    });
  }, [features, inputText, intl]);

  return (
    <div className={classes.orderCardType} style={{ display: hidden ? 'none' : '' }}>
      <div>
        {results.length > 0 && (
          <h3 className={classes.orderOptionListSubtitle}>
            <FormattedMessage id="orderOptionList.allTypes" />
          </h3>
        )}
      </div>
      <div className={classes.container}>
        {results.length > 0 ? (
          results.map(option => (
            <OrderCardType
              key={option}
              type={orderType.get(option)}
              hasHelp={hasHelp(option)}
              withDescription={withDescription}
              handleClick={() => handleOrderOptionSelect(orderType.get(option))}
            />
          ))
        ) : (
          <div style={{ marginBottom: '40px' }}>
            <FormattedMessage id="orderOptionList.noResults" />
          </div>
        )}
      </div>
    </div>
  );
};

const OrderOptionsList = ({ features, handleOrderOptionSelect, withDescription }) => {
  const { activeIntegrations = [] } = useUser();
  const [inputActive, setInputActive] = useState(false);
  const [inputText, setInputText] = useState('');
  const [hasPermissionIzee, setHasPermissionIzee] = useState(false);
  const [isLoadingIzee, setIsLoadingIzee] = useState(false);
  const intl = useIntl();
  const me = useMemo(() => JSON.parse(sessionStorage.getItem('me')), []);
  const country = me?.profile?.country || 'BR';

  const hasHelp = useCallback(option => {
    const includeHelp = ['VISTORIA_DOCUSIGN'];
    return includeHelp.includes(option);
  }, []);

  const handleRedirectToIzee = useCallback(() => {
    const accessToken = sessionStorage.getItem('accessToken');
    const tokenType = sessionStorage.getItem('tokenType');
    window.open(
      `${APP_REDE_IZEE_URL}/#access_token=${accessToken}&token_type=${tokenType}&expires_in=`,
      '_blank'
    );
  }, []);

  const debounceSave = useCallback(
    debounce(value => setInputText(value), 300),
    []
  );

  const handleSearchInput = event => {
    const { value } = event.target;
    debounceSave(value);
  };

  useEffect(() => {
    const cachedPermission = sessionStorage.getItem('hasPermissionIzee');
    if (cachedPermission !== null) {
      setHasPermissionIzee(JSON.parse(cachedPermission));
      return;
    }

    if (me?.profile?.email) {
      setIsLoadingIzee(true);
      postCheckUser(me.profile.email)
        .then(result => {
          if (result?.data?.data?.exists) {
            setHasPermissionIzee(true);
            sessionStorage.setItem('hasPermissionIzee', true);
          } else {
            sessionStorage.setItem('hasPermissionIzee', false);
          }
        })
        .catch(error => {
          console.error('Error on postCheckUser:', error);
        })
        .finally(() => {
          setIsLoadingIzee(false);
        });
    }
  }, [me]);

  return (
    <div className={classes.orderOptionsList}>
      <div className={classes.containerHead}>
        <div className={classes.title}>
          <Add style={{ fontSize: 30, marginRight: 8, color: '#393939' }} />
          <h1>
            <FormattedMessage id="OrderOptionsList.newOrder" />
          </h1>
        </div>
        
        <div className="row">
          <div className="col-sm-12 col-lg-5 text-left" style={{ position: 'relative' }}>
            <Search
              className={classes.iconSearch}
              style={{ display: inputActive ? 'none' : 'block' }}
            />
            <input
              type="text"
              name="search"
              className={`form-control ${classes.inputSearch}`}
              placeholder={inputActive ? '' : intl.formatMessage({ id: 'orderOptionList.searchPlaceholder' })}
              style={{
                paddingLeft: inputActive ? '10px' : '40px',
                color: inputActive ? 'black' : 'gray',
                backgroundColor: inputActive ? 'white' : '',
              }}
              defaultValue={inputText}
              onChange={handleSearchInput}
              onFocus={() => setInputActive(true)}
              onBlur={() => {
                if (!inputText) {
                  setInputActive(false);
                }
              }}
            />
          </div>
        </div>
      </div>

      {features.some(feature => ['VISTORIA_ENTRADA', 'VISTORIA_SAIDA'].includes(feature)) && (
        <OrderMainRequests
          features={features}
          handleOrderOptionSelect={handleOrderOptionSelect}
          withDescription={withDescription}
          hidden={!!inputText}
        />
      )}

      <OrderSearch
        features={features}
        handleOrderOptionSelect={handleOrderOptionSelect}
        withDescription={withDescription}
        hidden={!inputText}
        inputText={inputText}
        intl={intl}
        hasHelp={hasHelp}
      />

      {!!withDescription && country === 'BR' && (
        <div className={classes.orderCardService}>
          <div>
            <h3 className={classes.orderOptionListSubtitle}>
              <FormattedMessage id="orderOptionList.additionalServices" />
            </h3>
          </div>
          <div className={classes.container}>
            <OrderCardService
              type={orderType.get('VISTORIA_TOUR_VIRTUAL')}
              hasPermission={features.includes('VISTORIA_TOUR_VIRTUAL')}
              hasNews={true}
              isLoading={false}
              handleClick={handleOrderOptionSelect}
            />
            <OrderCardService
              type={orderType.get('IZEE')}
              hasPermission={hasPermissionIzee}
              hasNews={false}
              isLoading={isLoadingIzee}
              handleClick={handleRedirectToIzee}
            />
            <OrderCardService
              type={orderType.get('VISTORIA_FICHA_CERTA')}
              hasPermission={features.includes('VISTORIA_FICHA_CERTA')}
              hasNews={false}
              isLoading={false}
              handleClick={handleOrderOptionSelect}
            />
            <OrderCardService
              type={orderType.get('VISTORIA_BRY')}
              hasPermission={features.includes('VISTORIA_BRY')}
              hasNews={false}
              isLoading={false}
              handleClick={handleOrderOptionSelect}
            />
            {me?.profile?.franchisee_features?.includes('ORDER_MARKETPLACE_REPARO_FACIL') && (
              <OrderCardService
                type={orderType.get('VISTORIA_REPARO_FACIL') || 'marketplace_reparofacil'}
                hasPermission={features.includes('VISTORIA_REPARO_FACIL') && activeIntegrations.includes('reparofacil')}
                hasNews={true}
                isLoading={false}
                handleClick={handleOrderOptionSelect}
              />
            )}
          </div>
        </div>
      )}

      <OrderAllTypes
        features={features}
        handleOrderOptionSelect={handleOrderOptionSelect}
        withDescription={withDescription}
        hidden={!!inputText}
        hasHelp={hasHelp}
      />
    </div>
  );
};

OrderOptionsList.propTypes = {
  handleOrderOptionSelect: PropTypes.func.isRequired,
  features: PropTypes.array.isRequired,
  withDescription: PropTypes.bool.isRequired,
};

export default OrderOptionsList;
